import {
  AspectRatio,
  AspectRatioProps,
  BoxProps,
  IconButtonProps,
  StackProps,
  VStack,
} from '@chakra-ui/react';
import { StaticImageData } from 'next/image';
import { FC, memo, useEffect, useState } from 'react';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/zoom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Swiper as SwiperType } from 'swiper/types';
import { useGlobalState } from '@/shared/providers';
import { ResponsiveValue } from '@/shared/types';
import Image from '../Image';
import ControlButton from './ControllButton';
import Thumbnails from './Thumbnails';

type Slide = {
  id: string;
  alt?: string;
} & (
  | {
      type: 'image';
      src: string | StaticImageData;
    }
  | {
      type: 'video';
      src: string;
      poster?: string | StaticImageData;
    }
);

const modalBodyStyles: StackProps = {
  sx: {
    '.swiper-wrapper': {
      top: 0,
      bottom: 0,
      position: 'absolute',
    },
    '.swiper-slide': {
      padding: 0,
    },
  },
};

type ProductGalleryWithThumbnailsProps = {
  slides: Slide[];
  activeImage?: number;
  controlsSize?: IconButtonProps['size'];
  imageFit?: 'fill' | 'contain' | 'cover' | 'none' | 'scale-down';
  imgSizes?: ResponsiveValue | string;
  previewBlockStyles?: BoxProps;
  activeImageStyles?: AspectRatioProps;
  unoptimized?: boolean;
} & StackProps;

const ProductGalleryWithThumbnails: FC<ProductGalleryWithThumbnailsProps> = ({
  slides,
  activeImage,
  controlsSize,
  imgSizes,
  imageFit = 'contain',
  previewBlockStyles,
  activeImageStyles,
  unoptimized = false,
  ...styles
}) => {
  const [swiperInstance, setSwiperInstance] = useState<SwiperType>();
  const [showNavigation, setShowNavigation] = useState(false);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const { isMobile } = useGlobalState();

  const showControls = !isMobile && slides.length > 1;

  const handleSlideChange = (swiper: SwiperType) => {
    setActiveImageIndex(swiper.activeIndex);
  };

  const handleNextClick = () => {
    swiperInstance?.slideNext();
  };

  const handlePrevClick = () => {
    swiperInstance?.slidePrev();
  };

  const onPreviewClick = (index: number) => {
    swiperInstance?.slideTo(index);
  };

  useEffect(() => {
    if (activeImage !== undefined) {
      setActiveImageIndex(activeImage);
      swiperInstance?.slideTo(activeImage);
    }
  }, [activeImage, swiperInstance]);
  return (
    <VStack
      {...modalBodyStyles}
      {...styles}
    >
      <AspectRatio
        borderRadius='xl'
        overflow='hidden'
        ratio={1 / 1}
        width='full'
        onMouseLeave={() => setShowNavigation(false)}
        onMouseOver={() => setShowNavigation(true)}
        {...activeImageStyles}
      >
        <Swiper
          initialSlide={activeImage}
          onSlideChange={handleSlideChange}
          onSwiper={setSwiperInstance}
        >
          {slides.map((slide, index) => (
            <SwiperSlide key={slide.id}>
              {({ isActive, isNext }) =>
                slide.type === 'image' ? (
                  <Image
                    alt={slide.alt}
                    aspectRatio={1}
                    imgSizes={imgSizes}
                    isEager={isActive || isNext}
                    objectFit={imageFit}
                    src={slide.src}
                    onClick={() => {
                      onPreviewClick?.(index);
                    }}
                  />
                ) : (
                  <></>
                  // TODO: Implement video support
                )
              }
            </SwiperSlide>
          ))}
          {showControls && (
            <>
              <ControlButton
                disabled={activeImageIndex === 0}
                showNavigation={showNavigation}
                size={controlsSize}
                type='prev'
                onClick={handlePrevClick}
              />
              <ControlButton
                disabled={activeImageIndex === slides.length - 1}
                showNavigation={showNavigation}
                size={controlsSize}
                type='next'
                onClick={handleNextClick}
              />
            </>
          )}
        </Swiper>
      </AspectRatio>
      {slides.length > 1 && (
        <Thumbnails
          flexShrink={0}
          images={slides.map((slide) => ({
            id: slide.id,
            alt: slide.alt,
            src: slide.type === 'image' ? slide.src : slide.poster || '',
          }))}
          imgSizes={{ base: '6rem', md: '8rem' }}
          maxW='full'
          selectedImageIndex={activeImageIndex}
          unoptimized={unoptimized}
          w='max-content'
          onImageClick={onPreviewClick}
          {...previewBlockStyles}
        />
      )}
    </VStack>
  );
};

export type { Slide, ProductGalleryWithThumbnailsProps };
export default memo(ProductGalleryWithThumbnails);
